import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { useAuth } from '../../../context/auth-context'
import { OneOffPayment, OneOffPaymentInput } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { FormItem, InputNumber, FormikTextArea } from '../../../components/formik-component'
import { FormikPaymentCycleDropDown } from '../../../components/formik-component/PaymentCycleDropDown'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'
import useMutateOneOffPayment from '../../../graphql/one-off-payment/hooks/useMutateOneOffPayment'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useCurrency } from '../../../context/currency-context'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import PerFlexRow from '../../../components/custom_components/FlexRow'
interface Props {
  open: boolean
  isDeleting: boolean
  oneOffPayment?: OneOffPayment
  meetingId: number
  nextPaymentcycle: string
  toggleDeleting: React.Dispatch<React.SetStateAction<boolean>>
  toggleOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const OneOffPaymentModal: FC<Props> = ({
  open,
  isDeleting,
  oneOffPayment,
  toggleDeleting,
  toggleOpen,
  meetingId,
  nextPaymentcycle,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatCurrencySymbol } = useAmountAndCurrency(user.locale)
  const { currency } = useCurrency()

  const [error, setError] = useState<string | null>(null)

  // to do technically not correct here it could be that reviewer and managers are in different countries and use different currencies
  const { deleteOneOffPayment, saveOneOffPayment } = useMutateOneOffPayment(meetingId)

  const mapPropsToInitialState = () => {
    return {
      comment: oneOffPayment ? oneOffPayment.comment : '',
      resultAmount: oneOffPayment ? oneOffPayment.resultAmount : '',
      paymentDate: oneOffPayment ? oneOffPayment.paymentDate : nextPaymentcycle,
      meetingId: meetingId,
      id: oneOffPayment ? oneOffPayment.id : undefined,
    } as OneOffPaymentInput
  }
  const [initialFormState, setInitialFormState] = useState(mapPropsToInitialState())

  useEffect(() => {
    setInitialFormState(mapPropsToInitialState())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneOffPayment])

  const getNotificationOperation = () => {
    if (isDeleting) {
      return t('appraisalMeeting.oneOffPayment.notifications.operations.deleted')
    }
    if (!!oneOffPayment && !!oneOffPayment.id) {
      return t('appraisalMeeting.oneOffPayment.notifications.operations.edited')
    } else {
      return t('appraisalMeeting.oneOffPayment.notifications.operations.created')
    }
  }

  const saveOrUpdateOneOff = async (values: { comment: string; paymentDate: string; resultAmount: number }) => {
    const oneOffInput: OneOffPaymentInput = {
      meetingId: meetingId,
      comment: values.comment,
      paymentDate: values.paymentDate,
      resultAmount: values.resultAmount,
      id: oneOffPayment ? oneOffPayment.id : undefined,
    }
    const oneOff = await saveOneOffPayment(oneOffInput)
    if (!!oneOff.errors && !!oneOff.errors.length) {
      setError('appraisalMeeting.oneOffPayment.notifications.saveErrorMessage')
    } else {
      showNotification({
        type: 'success',
        message: t('appraisalMeeting.oneOffPayment.notifications.success.title'),
        description: t('appraisalMeeting.oneOffPayment.notifications.success.description', {
          operation: getNotificationOperation(),
        }),
      })
      toggleOpen(false)
    }
  }

  const deleteOneOff = async (): Promise<void> => {
    if (!oneOffPayment) {
      return
    }
    const { errors } = await deleteOneOffPayment(oneOffPayment.id)
    if (!!errors && !!errors.length) {
      setError('appraisalMeeting.oneOffPayment.notifications.deletionErrorMessage')
    } else {
      showNotification({
        type: 'success',
        message: t('appraisalMeeting.oneOffPayment.notifications.success.title'),
        description: t('appraisalMeeting.oneOffPayment.notifications.success.description', {
          operation: getNotificationOperation(),
        }),
      })
      toggleDeleting(false)
      toggleOpen(false)
    }
  }

  return (
    <Formik
      initialValues={{ ...initialFormState }}
      enableReinitialize={true}
      onSubmit={async (values: { comment: string; paymentDate: string; resultAmount: number }, { setSubmitting, resetForm }: any) => {
        if (isDeleting) {
          await deleteOneOff()
        } else {
          await saveOrUpdateOneOff(values)
        }
        resetForm()
        setSubmitting(false)
      }}
      validateOnBlur={true}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        resultAmount: Yup.number()
          .moreThan(
            0,
            t('formError.minValue', {
              fieldName: t('appraisalMeeting.oneOffPayment.amount'),
              minAmount: 0,
              currency: formatCurrencySymbol(currency),
            }),
          )
          .lessThan(Number.POSITIVE_INFINITY, t('formError.numberInvalid'))
          .required(t('formError.required', { fieldName: t('appraisalMeeting.oneOffPayment.amount') }))
          .nullable(),
        comment: Yup.string()
          .min(2, t('formError.commentLength'))
          .required(t('formError.required', { fieldName: t('appraisalMeeting.oneOffPayment.comment') })),
      })}
    >
      {(props) => {
        const { isSubmitting, handleSubmit, resetForm, values } = props

        return (
          <PerModal
            title={{ heading: t('appraisalMeeting.oneOffPayment.title') }}
            visible={open}
            onCancel={() => {
              resetForm()
              toggleDeleting(false)
              toggleOpen(false)
            }}
            footer={{
              formikForm: {
                onSubmit: handleSubmit,
              },
              confirmBtn: {
                text: isDeleting ? t('global.button.delete') : t('global.button.save'),
                disabled: isSubmitting || !values.comment || values.comment.length < 2 || !values.resultAmount,
                isProcessing: { active: isSubmitting },
                eventHandler: { onMouseDown: handleSubmit },
                tooltip: isSubmitting
                  ? t('appraisalMeeting.oneOffPayment.tooltip.submitting')
                  : values.resultAmount === 0
                  ? t('appraisalMeeting.oneOffPayment.tooltip.disabled1')
                  : !values.resultAmount
                  ? t('appraisalMeeting.oneOffPayment.tooltip.disabled2')
                  : !values.comment
                  ? t('appraisalMeeting.oneOffPayment.tooltip.disabled3')
                  : values.comment.length < 2
                  ? t('appraisalMeeting.oneOffPayment.tooltip.disabled4')
                  : undefined,
              },
            }}
          >
            <div data-e2e-oneoffpayment-form>
              {isDeleting && (
                <PerSpacer mb="l">
                  <PerFlexRow>
                    <PerText fontWeight="bold">{t('appraisalMeeting.oneOffPayment.deleteDescription')}</PerText>
                  </PerFlexRow>
                </PerSpacer>
              )}

              <PerSpacer mb="xs">
                <PerText appearance="label-small" display="block">
                  {t('appraisalMeeting.oneOffPayment.paymentDate')}:
                </PerText>
                <FormItem name="paymentDate">
                  <FormikPaymentCycleDropDown
                    name="paymentDate"
                    earliestCycle={nextPaymentcycle}
                    disabled={isDeleting}
                    data-e2e-oneoffpayment-paymentdateselect
                  />
                </FormItem>
              </PerSpacer>

              <PerSpacer mb="xs">
                <PerText appearance="label-small" display="block">
                  {t('appraisalMeeting.oneOffPayment.toBePaid')}:
                </PerText>
                <FormItem name="resultAmount">
                  <InputNumber
                    data-e2e-oneoffpayment-input
                    name="resultAmount"
                    type="currency"
                    timezoneAndLocale={user}
                    currency={currency}
                    step={50}
                    min={0}
                    placeholder="0"
                    disabled={isDeleting}
                  />
                </FormItem>
              </PerSpacer>

              <div>
                <PerText appearance="label-small" display="block">
                  {t('appraisalMeeting.oneOffPayment.comment')}:
                </PerText>
                <FormItem name="comment">
                  {isDeleting && !!oneOffPayment && !!oneOffPayment.comment && (
                    <div className="per-noteItem--text" data-e2e-noteslist>
                      <PerText appearance="flowing-text" mt="xxs">
                        {oneOffPayment.comment}
                      </PerText>
                    </div>
                  )}
                  {!isDeleting && (
                    <FormikTextArea
                      placeholder={t('appraisalMeeting.oneOffPayment.commentPlaceholder')}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      name="comment"
                      data-e2e-oneoffpayment-textarea
                    />
                  )}
                </FormItem>
              </div>
              {error && (
                <PerText color="error" fontSize="m" mt="m">
                  {error}
                </PerText>
              )}
            </div>
          </PerModal>
        )
      }}
    </Formik>
  )
}
